import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import { getContext } from '@/context';
import withTimeout from '@/utils/async/withTimeout';

/**
 * Web Share API — correct on mobile web, but a browser API: Android's WebView
 * doesn't implement it and iOS `WKWebView` is inconsistent, so inside the native
 * apps it is often simply absent.
 */
const shareViaBrowser = async (url: string): Promise<boolean> => {
  if (!navigator.share) return false;

  try {
    await navigator.share({ url });
    return true;
  } catch (error) {
    // The user closed the sheet — it opened, which is all this had to do.
    // `NotAllowedError` instead means the tap's activation had already lapsed.
    return (error as Error)?.name === 'AbortError';
  }
};

/**
 * Asks the host to open the OS sheet — the transport a WebView needs once the
 * browser API is unavailable.
 *
 * `handlers.share` resolves with `IResolveResponse` and never rejects, so
 * `success` is the only signal that the host took the action; treating any
 * settled promise as a share would strand the user on a dead button. It never
 * times out either, so an unhandled action would stay pending forever — hence
 * the bound.
 */
const shareViaHost = async (url: string): Promise<boolean> => {
  try {
    return await withTimeout({
      promise: getContext()
        .handlers.share({ url })
        .then((response) => Boolean(response?.success)),
      timeoutMs: 2500,
      fallback: false,
    });
  } catch (error) {
    errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);
    return false;
  }
};

/**
 * Offers a link through the OS share sheet. `false` means neither transport was
 * reachable, so the caller falls back to the Picsart sheet.
 */
const shareNatively = async (url: string): Promise<boolean> => {
  if (await shareViaBrowser(url)) return true;
  if (await shareViaHost(url)) return true;

  // Reported once, not per transport: on native the browser API is expected to
  // be missing, and a first share can legitimately lose its activation — only
  // both transports declining is worth knowing about.
  errorLogger.trigger(
    new Error('[presetShare] no native share transport'),
    BUGSNAG_STATUSES.WARN
  );
  return false;
};

export default shareNatively;
